<div class="project-overview-page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Project Command Center</p>
      <h2>Overview</h2>
      <p class="subtitle">Track delivery health, priorities, documents, and momentum from one place.</p>
    </div>
    <div class="header-actions">
      <label class="project-picker">
        <span>Project</span>
        <select [(ngModel)]="selectedProjectId" (ngModelChange)="loadOverview()">
          <option [ngValue]="null">Select project</option>
          <option *ngFor="let project of projects" [ngValue]="project.id">{{ project.name }}</option>
        </select>
      </label>
      <button mat-stroked-button (click)="loadProjects()">Refresh</button>
    </div>
  </div>

  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

  <ng-container *ngIf="selectedProject as project; else emptyState">
    <section class="hero-card">
      <div class="hero-copy">
        <span class="status-badge" [ngClass]="healthClass">{{ healthLabel }}</span>
        <h3>{{ project.name }}</h3>
        <p>{{ project.description || 'No project summary has been added yet.' }}</p>
        <div class="hero-meta">
          <span>Lead: {{ project.leadStaffName || 'Unassigned' }}</span>
          <span>Status: {{ project.status || 'Unknown' }}</span>
          <span>Team: {{ project.teamMembers?.length || 0 }}</span>
        </div>
      </div>
      <div class="hero-progress">
        <div class="progress-ring">
          <strong>{{ project.progressPercent || 0 | number:'1.0-0' }}%</strong>
          <span>Delivery Progress</span>
        </div>
        <div class="progress-track">
          <div class="progress-fill" [style.width.%]="project.progressPercent || 0"></div>
        </div>
      </div>
    </section>

    <section class="stats-grid">
      <article class="stat-card">
        <span>Tasks Completed</span>
        <strong>{{ completedTasks }}/{{ tasks.length }}</strong>
        <small>{{ completionRate }}% completion rate</small>
      </article>
      <article class="stat-card critical">
        <span>Overdue Work</span>
        <strong>{{ overdueTasks }}</strong>
        <small>Items that need immediate follow-up</small>
      </article>
      <article class="stat-card warning">
        <span>Blocked Tasks</span>
        <strong>{{ blockedTasks }}</strong>
        <small>Tasks waiting on decisions or resources</small>
      </article>
      <article class="stat-card">
        <span>Documents & Media</span>
        <strong>{{ documentCount }}</strong>
        <small>Project files, task files, and onboarding records</small>
      </article>
    </section>

    <section class="content-grid">
      <article class="panel">
        <div class="panel-head">
          <h3>Upcoming Deadlines</h3>
          <span>{{ upcomingItems.length }} queued</span>
        </div>
        <div class="timeline-list" *ngIf="upcomingItems.length; else noUpcoming">
          <div class="timeline-item" *ngFor="let task of upcomingItems">
            <div class="timeline-dot"></div>
            <div>
              <strong>{{ task.title }}</strong>
              <p>{{ task.description || 'No task description provided.' }}</p>
              <small>{{ task.assigneeName || 'Unassigned' }} • Due {{ task.dueDate | date:'mediumDate' }}</small>
            </div>
          </div>
        </div>
      </article>

      <article class="panel">
        <div class="panel-head">
          <h3>Recent Activity</h3>
          <span>Latest 8 updates</span>
        </div>
        <div class="activity-list" *ngIf="recentActivity.length; else noActivity">
          <div class="activity-item" *ngFor="let item of recentActivity">
            <span class="activity-type">{{ item.type }}</span>
            <div>
              <strong>{{ item.label }}</strong>
              <small>{{ item.when | date:'medium' }}</small>
            </div>
          </div>
        </div>
      </article>

      <article class="panel compact">
        <div class="panel-head">
          <h3>Project Readiness</h3>
          <span>{{ onboarding.length }} records</span>
        </div>
        <div class="checklist">
          <div class="checklist-item" *ngFor="let item of onboarding | slice:0:6">
            <div class="check-icon" [ngClass]="{ done: item.isCompleted || item.status === 'Completed' }">
              <mat-icon>{{ item.isCompleted || item.status === 'Completed' ? 'check' : 'schedule' }}</mat-icon>
            </div>
            <div>
              <strong>{{ item.title }}</strong>
              <small>{{ item.updatedAt || item.createdAt | date:'mediumDate' }}</small>
            </div>
          </div>
          <p class="muted" *ngIf="!onboarding.length">No onboarding checklist or document records yet.</p>
        </div>
      </article>

      <article class="panel compact">
        <div class="panel-head">
          <h3>Execution Snapshot</h3>
          <span>Current workload</span>
        </div>
        <div class="snapshot-row">
          <span>Open Tasks</span>
          <strong>{{ tasks.length - completedTasks }}</strong>
        </div>
        <div class="snapshot-row">
          <span>Completed Tasks</span>
          <strong>{{ completedTasks }}</strong>
        </div>
        <div class="snapshot-row">
          <span>Attached Media</span>
          <strong>{{ selectedProject.mediaAttachments?.length || 0 }}</strong>
        </div>
        <div class="snapshot-row">
          <span>Last Update</span>
          <strong>{{ selectedProject.updatedAt || selectedProject.createdAt | date:'mediumDate' }}</strong>
        </div>
      </article>
    </section>
  </ng-container>
</div>

<ng-template #emptyState>
  <section class="empty-state">
    <mat-icon>workspaces</mat-icon>
    <h3>No project selected</h3>
    <p>Create a project or choose one from the selector to view its command center.</p>
  </section>
</ng-template>

<ng-template #noUpcoming>
  <p class="muted">No task deadlines are currently scheduled.</p>
</ng-template>

<ng-template #noActivity>
  <p class="muted">No recent activity has been recorded yet.</p>
</ng-template>
